/**
 * The arithmetic behind a table column a person can drag wider or narrower.
 *
 * The owner, 2026-10-19: "columns in gojib must be adjustable". The inbox's
 * panes already moved (`pane-width-model.ts`, `ResizeHandle.tsx`), so this is
 * the same idea carried to every table: the handle is the inbox's, and the
 * numbers it produces for a column are decided here, as plain functions, where
 * they can be tested without a DOM.
 *
 * TWO KINDS OF TABLE, ONE MODEL. A table that fits its card (`fit`) keeps its
 * total width whatever happens, so the line between two columns moves the way
 * the inbox's lines do: the column on the left takes what the one on the right
 * gives, and the last column has no handle because its right edge is the card.
 * A table allowed to scroll sideways sizes each column on its own and grows or
 * shrinks with the sum.
 *
 * WIDTHS ARE SEEDED FROM WHAT THE BROWSER DREW. Until somebody drags, the
 * browser lays the columns out as it always did. The first drag reads every
 * column's drawn width and keeps the lot, with the one that moved changed, so
 * the table switches to fixed widths without any column jumping. A later drag
 * reads the drawn widths again, so a column the browser stretched to fill a
 * wider window is dragged from where it is, not from a number nobody can see.
 */
import type { PaneBounds } from './pane-width-model'

/** The least a column is drawn at: room for a short header beside its sort arrow. */
export const COLUMN_MIN_PX = 80

/** The most a column in a scrolling table can be dragged to. */
export const COLUMN_MAX_PX = 1200

/** What a column the table has no width for yet is given, which is roughly a name. */
export const COLUMN_DEFAULT_PX = 160

/** The width of each column by its key. A table without an entry for a key draws that column at the default. */
export type ColumnWidths = Readonly<Record<string, number>>

/** The storage shape the model needs. A test hands in a map and a page hands in `localStorage`. */
export type ColumnWidthStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** A width inside the bounds, rounded to a whole pixel. Anything that is not a number reads as the least. */
export function clampColumnWidth(width: number, min: number = COLUMN_MIN_PX, max: number = COLUMN_MAX_PX): number {
  if (!Number.isFinite(width)) return min
  return Math.round(Math.min(Math.max(min, max), Math.max(min, width)))
}

/**
 * Whether the column at `index` has a handle. In a fitted table the last
 * column's right edge is the card's, so there is nothing to drag there.
 */
export function columnHasHandle(count: number, index: number, fit: boolean): boolean {
  if (index < 0 || index >= count) return false
  return !fit || index < count - 1
}

/**
 * The bounds a column's handle moves within, from the drawn widths.
 *
 * In a fitted table the column can take everything its right neighbour has
 * above the least, and no more, because the neighbour has to stay readable. In
 * a scrolling table it goes to the ceiling. `initial` is where the column is
 * now, which is what the handle reports before anybody moves it. Null for a
 * column with no handle, or one the table has not measured yet.
 */
export function columnHandleBounds(measured: readonly number[], index: number, fit: boolean): PaneBounds | null {
  if (!columnHasHandle(measured.length, index, fit)) return null
  const width = measured[index]
  if (width === undefined) return null
  const max = fit ? Math.max(COLUMN_MIN_PX, width + (measured[index + 1] ?? 0) - COLUMN_MIN_PX) : COLUMN_MAX_PX
  return { min: COLUMN_MIN_PX, max, initial: Math.round(width) }
}

/**
 * Every column's width after the one at `index` is set to `width`.
 *
 * Every key takes its drawn width, so the table can switch to fixed widths
 * without a jump. The column that moved is held to its bounds. In a fitted
 * table its right neighbour takes the difference, so the total is what it was.
 * A column with no handle changes nothing.
 */
export function resizeColumn(
  measured: readonly number[],
  keys: readonly string[],
  index: number,
  width: number,
  fit: boolean,
): ColumnWidths {
  const next: Record<string, number> = {}
  keys.forEach((key, at) => {
    const drawn = measured[at]
    next[key] = drawn === undefined ? COLUMN_DEFAULT_PX : Math.round(drawn)
  })
  const bounds = columnHandleBounds(measured, index, fit)
  const key = keys[index]
  if (bounds === null || key === undefined) return next
  const value = clampColumnWidth(width, bounds.min, bounds.max)
  next[key] = value
  const neighbour = keys[index + 1]
  if (fit && neighbour !== undefined) {
    next[neighbour] = Math.max(COLUMN_MIN_PX, (next[neighbour] ?? COLUMN_DEFAULT_PX) + bounds.initial - value)
  }
  return next
}

/**
 * The widths a table draws, one per key, in order.
 *
 * A key with no stored width gets the default. With `available` room (a fitted
 * table's card, less any column the table sizes itself), widths that would
 * overflow it are scaled down together, so a split made in a wide window still
 * fits a narrow one rather than running off the card's edge. Nothing is scaled
 * up: a table narrower than its room is stretched by the browser, which is what
 * a fitted table always did. Null room means no scaling, which is what a
 * scrolling table asks for.
 */
export function fitColumnWidths(widths: ColumnWidths, keys: readonly string[], available: number | null): number[] {
  const wanted = keys.map((key) => {
    const width = widths[key]
    return width === undefined ? COLUMN_DEFAULT_PX : clampColumnWidth(width, COLUMN_MIN_PX, Number.POSITIVE_INFINITY)
  })
  if (available === null || !Number.isFinite(available) || available <= 0) return wanted
  const sum = wanted.reduce((total, width) => total + width, 0)
  if (sum <= available) return wanted
  const scale = available / sum
  return wanted.map((width) => Math.max(COLUMN_MIN_PX, Math.floor(width * scale)))
}

/**
 * The style a header cell takes for a width a person dragged.
 *
 * The width alone lost to a column's own `max-w-*` or `min-w-*` class, and a
 * header cell's max-width holds the column in a fixed-layout table. Dragging
 * Email on Contacts (capped at 140px) wider by 100px widened the table by 100px
 * and left Email where it was, so the browser handed the 100px to every column
 * at once (owner, 2026-10-22: "when i increase the size on one, the size of all
 * of them increases"). Inline min and max equal to the width win over any class.
 */
export function columnWidthStyle(width: number): { width: string; minWidth: string; maxWidth: string } {
  const px = `${String(width)}px`
  return { width: px, minWidth: px, maxWidth: px }
}

/** Where one table's widths are kept, beside its hidden columns. */
export function columnWidthsKey(tableKey: string): string {
  return `gojibasel.table.${tableKey}.widths`
}

/**
 * The key a table's widths are kept under: the table's own key when a page
 * gave it one, else its caption with the spaces and punctuation folded, which
 * every `DataTable` has and which names the table well enough for a browser
 * preference. Two tables with one caption share one memory, which is also what
 * a person expects of two tables that say they are the same thing.
 */
export function tableWidthsKey(tableKey: string | undefined, caption: string): string {
  if (tableKey !== undefined && tableKey !== '') return tableKey
  const folded = caption
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `caption.${folded === '' ? 'table' : folded}`
}

/**
 * A stored value, read back as widths.
 *
 * Anything that is not an object of finite positive numbers is dropped rather
 * than trusted, key by key: a value an older build wrote or somebody edited by
 * hand reads as a table that still draws, at its defaults where it has to.
 */
export function parseColumnWidths(raw: string | null | undefined): ColumnWidths {
  if (raw === null || raw === undefined || raw === '') return {}
  try {
    const value: unknown = JSON.parse(raw)
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return {}
    const widths: Record<string, number> = {}
    for (const [key, width] of Object.entries(value)) {
      if (key !== '' && typeof width === 'number' && Number.isFinite(width) && width > 0) {
        widths[key] = Math.round(width)
      }
    }
    return widths
  } catch {
    return {}
  }
}

/** The stored widths, or none. Never throws: a browser that blocks site data reads as a table never resized. */
export function readColumnWidths(store: ColumnWidthStore | undefined, tableKey: string): ColumnWidths {
  try {
    return parseColumnWidths(store?.getItem(columnWidthsKey(tableKey)))
  } catch {
    return {}
  }
}

/**
 * Keeps the widths, or forgets them when there are none, which is what a
 * double click on a handle leaves behind. Returns whether the write landed. A
 * blocked store is not an error worth showing anybody: the widths last as long
 * as the page does.
 */
export function writeColumnWidths(store: ColumnWidthStore | undefined, tableKey: string, widths: ColumnWidths): boolean {
  if (store === undefined) return false
  try {
    if (Object.keys(widths).length === 0) store.removeItem(columnWidthsKey(tableKey))
    else store.setItem(columnWidthsKey(tableKey), JSON.stringify(widths))
    return true
  } catch {
    return false
  }
}

/** The browser's storage, or nothing where reaching for it throws. */
export function columnWidthStore(): ColumnWidthStore | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    return undefined
  }
}
